import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { listData, updateData, createData, streamAI } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Copy, PaperPlaneTilt, CaretDown, Code, ListChecks, Sparkle, Storefront,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const CAT_ORDER = ["Homepage", "Product Page", "Cart", "Technical"];

export default function ConversionOptimizer() {
  const { activeId } = useClient();
  const qc = useQueryClient();
  const key = ["data", "cro_blocks", activeId];
  const { data: blocks = [] } = useQuery({ queryKey: key, queryFn: () => listData("cro_blocks", activeId), enabled: !!activeId });
  const [openId, setOpenId] = useState(null);
  const [aiFor, setAiFor] = useState(null);
  const [aiBrief, setAiBrief] = useState("");
  const [aiOut, setAiOut] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const inv = () => qc.invalidateQueries({ queryKey: key });

  const statusM = useMutation({
    mutationFn: ({ id, status }) => updateData("cro_blocks", id, { status }),
    onSuccess: () => inv(),
  });
  const approvalM = useMutation({
    mutationFn: (b) => createData("approvals", {
      client_id: activeId, title: `CRO: ${b.title}`, type: "cro_block", platform: "Storefront",
      body: b.code, image_path: "", status: "pending", scheduled_at: "",
    }),
    onSuccess: () => toast.success("Sent to Approval Queue"),
  });

  const applied = blocks.filter((b) => b.status === "applied").length;
  const pct = blocks.length ? Math.round((applied / blocks.length) * 100) : 0;
  const cats = CAT_ORDER.filter((c) => blocks.some((b) => b.category === c));

  const runAI = async (b) => {
    setAiBusy(true); setAiOut("");
    try {
      await streamAI("/ai/generate-stream", {
        client_id: activeId, content_type: "promotion",
        brief: `Write conversion-focused storefront copy for a "${b.title}" section. ${aiBrief || b.description}. Return headline, subtext and button label options.`,
      }, (c) => setAiOut((o) => o + c));
    } catch (e) { toast.error("Generation failed"); }
    setAiBusy(false);
  };

  return (
    <PageContainer>
      <PageHeader eyebrow="Storefront CRO" title="Conversion Optimizer"
        subtitle="Ready-to-paste Shopify theme blocks, copy and a step-by-step apply checklist. Copy a snippet into your theme, or send copy to the Approval Queue first.">
        <div className="text-right"><div className="font-head text-2xl brand-text">{pct}%</div><div className="label-caps">applied</div></div>
      </PageHeader>

      <div className="card-matte p-4 mb-6">
        <div className="flex justify-between text-sm mb-2"><span className="text-[#cfcfcf]">Apply progress</span><span className="text-[#9a9a9a]">{applied}/{blocks.length} blocks</span></div>
        <Progress value={pct} className="h-2 bg-white/10 [&>div]:bg-[#6B3DFF]" />
      </div>

      <div className="space-y-7">
        {cats.map((cat) => (
          <div key={cat}>
            <div className="flex items-center gap-3 mb-3">
              <h3 className="font-head text-base font-semibold">{cat}</h3>
              <div className="h-px flex-1 bg-white/8" />
            </div>
            <div className="space-y-3">
              {blocks.filter((b) => b.category === cat).map((b) => {
                const open = openId === b.id;
                return (
                  <div key={b.id} className="card-matte overflow-hidden" data-testid="cro-block">
                    <div className="flex items-center gap-3 p-4 cursor-pointer" onClick={() => setOpenId(open ? null : b.id)}>
                      <div className="h-9 w-9 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                        {b.type === "checklist" ? <ListChecks size={18} className="brand-text" /> : <Code size={18} className="brand-text" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm flex items-center gap-2">{b.title}<StatusBadge value={b.priority} /></div>
                        <div className="text-xs text-[#9a9a9a] truncate">{b.description}</div>
                      </div>
                      <StatusBadge value={b.status} />
                      <CaretDown size={16} className={`text-[#7a7a7a] transition-transform ${open ? "rotate-180" : ""}`} />
                    </div>
                    {open && (
                      <div className="px-4 pb-4 space-y-3 border-t border-white/8 pt-3">
                        <div className="text-sm text-[#cfcfcf]"><span className="label-caps">Where to paste</span><p className="mt-1 text-[#9a9a9a]">{b.where}</p></div>
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="label-caps">{b.type === "checklist" ? "Steps" : "Code"}</span>
                            <Button data-testid="copy-code-btn" size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(b.code); toast.success("Copied"); }} className="h-7 text-[#9a9a9a] hover:text-white"><Copy size={14} className="mr-1" /> Copy</Button>
                          </div>
                          <pre className="bg-[#0D0D14] border border-white/10 rounded-lg p-3 text-[12px] text-[#d6d6d6] overflow-x-auto max-h-72 overflow-y-auto whitespace-pre-wrap">{b.code}</pre>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Select value={b.status} onValueChange={(v) => statusM.mutate({ id: b.id, status: v })}>
                            <SelectTrigger className="bg-[#10101A] border-white/10 w-40 h-9" data-testid="cro-status"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-[#14141F] border-white/10 text-white">
                              <SelectItem value="not_started">Not started</SelectItem>
                              <SelectItem value="in_review">In review</SelectItem>
                              <SelectItem value="applied">Applied</SelectItem>
                            </SelectContent>
                          </Select>
                          {b.type !== "checklist" && (
                            <Button data-testid="cro-to-approval-btn" size="sm" onClick={() => approvalM.mutate(b)} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white"><PaperPlaneTilt size={15} className="mr-1" /> Send to Approval</Button>
                          )}
                          <Button data-testid="cro-ai-btn" size="sm" variant="outline" onClick={() => { setAiFor(b); setAiBrief(""); setAiOut(""); }} className="border-white/15 bg-transparent hover:bg-white/5 text-white"><Sparkle size={15} className="mr-1 brand-text" /> AI copy</Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!aiFor} onOpenChange={(o) => !o && setAiFor(null)}>
        <DialogContent className="bg-[#14141F] border-white/10 text-white max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><Sparkle size={18} className="brand-text" weight="fill" /> AI copy · {aiFor?.title}</DialogTitle></DialogHeader>
          <Textarea value={aiBrief} onChange={(e) => setAiBrief(e.target.value)} rows={2} placeholder="Optional: extra angle (e.g. emphasize bold dark roast for focus)" className="bg-[#10101A] border-white/10" />
          <Button data-testid="cro-ai-generate-btn" onClick={() => runAI(aiFor)} disabled={aiBusy} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white"><Sparkle size={15} className={`mr-1.5 ${aiBusy ? "ai-thinking" : ""}`} weight="fill" /> {aiBusy ? "Generating…" : "Generate copy"}</Button>
          {aiOut && (
            <div className="space-y-2">
              <pre className="bg-[#0D0D14] border border-white/10 rounded-lg p-3 text-sm text-[#e0e0e0] whitespace-pre-wrap max-h-60 overflow-y-auto">{aiOut}</pre>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(aiOut); toast.success("Copied"); }} className="text-[#9a9a9a] hover:text-white"><Copy size={14} className="mr-1" /> Copy</Button>
                <Button size="sm" onClick={() => createData("approvals", { client_id: activeId, title: `CRO copy: ${aiFor.title}`, type: "cro_copy", platform: "Storefront", body: aiOut, image_path: "", status: "pending", scheduled_at: "" }).then(() => toast.success("Sent to Approval Queue"))} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white"><PaperPlaneTilt size={14} className="mr-1" /> To Approval</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
